import React, { useEffect, useMemo, useRef, useState } from 'react';
import './JsonEditor.css';

// Plain-textarea JSON editor with line numbers and collapsible { } / [ ] blocks.
// Folded blocks are shown as a single MARKER char; their content is kept aside
// (by offset in the displayed text) and restored when emitting the full value.

const MARKER = '⋯';

// Index of the bracket closing the one at openIdx (string-aware), or -1
const findClosing = (text, openIdx) => {
    let depth = 0;
    let inString = false;
    for (let i = openIdx; i < text.length; i++) {
        const ch = text[i];
        if (inString) {
            if (ch === '\\') i++;
            else if (ch === '"') inString = false;
        } else if (ch === '"') {
            inString = true;
        } else if (ch === '{' || ch === '[') {
            depth++;
        } else if (ch === '}' || ch === ']') {
            depth--;
            if (depth === 0) return i;
        }
    }
    return -1;
};

// Replace each fold marker (folds sorted by offset) with its hidden content
const expand = (text, folds) => {
    let out = '';
    let last = 0;
    folds.forEach(f => {
        out += text.slice(last, f.offset) + f.hidden;
        last = f.offset + 1;
    });
    return out + text.slice(last);
};

const lineStarts = (text) => {
    const starts = [0];
    for (let i = 0; i < text.length; i++) if (text[i] === '\n') starts.push(i + 1);
    return starts;
};

const countNewlines = (text) => (text.match(/\n/g) || []).length;

// Offset of the opening bracket ending the given line, or -1
const openBracketAtLineEnd = (display, start, end) => {
    const trimmed = display.slice(start, end).replace(/\s+$/, '');
    const last = trimmed[trimmed.length - 1];
    return last === '{' || last === '[' ? start + trimmed.length - 1 : -1;
};

const foldLine = (state, lineIdx) => {
    const { display, folds } = state;
    const starts = lineStarts(display);
    const end = lineIdx + 1 < starts.length ? starts[lineIdx + 1] - 1 : display.length;
    const openIdx = openBracketAtLineEnd(display, starts[lineIdx], end);
    if (openIdx === -1) return state;
    const closeIdx = findClosing(display, openIdx);
    if (closeIdx === -1 || !display.slice(openIdx, closeIdx).includes('\n')) return state;

    const inner = folds
        .filter(f => f.offset > openIdx && f.offset < closeIdx)
        .map(f => ({ ...f, offset: f.offset - (openIdx + 1) }));
    const hidden = expand(display.slice(openIdx + 1, closeIdx), inner);
    const delta = 1 - (closeIdx - openIdx - 1);
    return {
        display: display.slice(0, openIdx + 1) + MARKER + display.slice(closeIdx),
        folds: [
            ...folds.filter(f => f.offset < openIdx),
            { offset: openIdx + 1, hidden },
            ...folds.filter(f => f.offset >= closeIdx).map(f => ({ ...f, offset: f.offset + delta })),
        ],
    };
};

const unfold = (state, fold) => {
    const { display, folds } = state;
    const delta = fold.hidden.length - 1;
    return {
        display: display.slice(0, fold.offset) + fold.hidden + display.slice(fold.offset + 1),
        folds: folds
            .filter(f => f.offset !== fold.offset)
            .map(f => (f.offset > fold.offset ? { ...f, offset: f.offset + delta } : f)),
    };
};

// Map a single contiguous textarea edit onto the fold offsets
const applyEdit = (state, nextDisplay) => {
    const prev = state.display;
    const max = Math.min(prev.length, nextDisplay.length);
    let prefix = 0;
    while (prefix < max && prev[prefix] === nextDisplay[prefix]) prefix++;
    let suffix = 0;
    while (suffix < max - prefix && prev[prev.length - 1 - suffix] === nextDisplay[nextDisplay.length - 1 - suffix]) suffix++;
    const removedEnd = prev.length - suffix;
    const delta = nextDisplay.length - prev.length;
    return {
        display: nextDisplay,
        folds: state.folds
            // a fold whose marker was deleted loses its content, like deleting a folded block
            .filter(f => f.offset < prefix || f.offset >= removedEnd)
            .map(f => (f.offset >= removedEnd ? { ...f, offset: f.offset + delta } : f)),
    };
};

const JsonEditor = ({ value, onChange, height = 420, hasError = false, error = '', fullscreenActions = null }) => {
    const [state, setState] = useState({ display: value || '', folds: [] });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const lastEmittedRef = useRef(value || '');
    const gutterRef = useRef(null);

    // External value changes (e.g. loading the current config) reset the folds
    useEffect(() => {
        if ((value || '') !== lastEmittedRef.current) {
            lastEmittedRef.current = value || '';
            setState({ display: value || '', folds: [] });
        }
    }, [value]);

    const handleChange = (e) => {
        const next = applyEdit(state, e.target.value);
        const full = expand(next.display, next.folds);
        setState(next);
        lastEmittedRef.current = full;
        if (onChange) onChange(full);
    };

    const lines = useMemo(() => {
        const { display, folds } = state;
        const starts = lineStarts(display);
        let realLine = 1;
        return starts.map((start, i) => {
            const end = i + 1 < starts.length ? starts[i + 1] - 1 : display.length;
            const lineFolds = folds.filter(f => f.offset >= start && f.offset <= end);
            const fold = lineFolds.find(f => display[f.offset - 1] === '{' || display[f.offset - 1] === '[');
            const line = {
                number: realLine,
                fold,
                foldable: !fold && openBracketAtLineEnd(display, start, end) !== -1,
            };
            realLine += 1 + lineFolds.reduce((sum, f) => sum + countNewlines(f.hidden), 0);
            return line;
        });
    }, [state]);

    const toggleLine = (i) => {
        const line = lines[i];
        if (line.fold) setState(s => unfold(s, line.fold));
        else if (line.foldable) setState(s => foldLine(s, i));
    };

    // Fold every block below the root (bottom-up) so top-level keys stay visible
    const foldAll = () => setState(s => {
        let next = s;
        for (let i = lineStarts(s.display).length - 1; i > 0; i--) next = foldLine(next, i);
        return next;
    });
    const unfoldAll = () => setState(s => ({ display: expand(s.display, s.folds), folds: [] }));

    // Lock page scroll while the editor covers the viewport
    useEffect(() => {
        if (!isFullscreen) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [isFullscreen]);

    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && isFullscreen) {
            e.stopPropagation();
            setIsFullscreen(false);
        }
    };

    const handleScroll = (e) => {
        if (gutterRef.current) gutterRef.current.scrollTop = e.target.scrollTop;
    };

    return (
        <div
            className={`rox-json-editor${hasError ? ' rox-json-editor--error' : ''}${isFullscreen ? ' rox-json-editor--fullscreen' : ''}`}
            onKeyDown={handleKeyDown}
        >
            <div className="rox-json-editor__toolbar">
                <div className="rox-json-editor__toolbar-group">
                    <button type="button" onClick={foldAll}>Collapse all</button>
                    <button type="button" onClick={unfoldAll}>Expand all</button>
                    <button type="button" onClick={() => setIsFullscreen(f => !f)} title={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'}>
                        {isFullscreen ? 'Exit full screen' : 'Full screen'}
                    </button>
                </div>
                {isFullscreen && fullscreenActions && (
                    <div className="rox-json-editor__toolbar-group">{fullscreenActions}</div>
                )}
            </div>
            {isFullscreen && error && <div className="rox-json-editor__error">{error}</div>}
            <div className="rox-json-editor__body" style={isFullscreen ? undefined : { height }}>
                <div className="rox-json-editor__gutter" ref={gutterRef}>
                    {lines.map((line, i) => (
                        <div key={i} className="rox-json-editor__gutter-line">
                            <span className="rox-json-editor__line-number">{line.number}</span>
                            <span
                                className={`rox-json-editor__fold-toggle${line.fold || line.foldable ? ' is-active' : ''}`}
                                onClick={() => toggleLine(i)}
                                title={line.fold ? 'Expand' : line.foldable ? 'Collapse' : undefined}
                            >
                                {line.fold ? '▸' : line.foldable ? '▾' : ''}
                            </span>
                        </div>
                    ))}
                </div>
                <textarea
                    className="rox-json-editor__textarea"
                    value={state.display}
                    onChange={handleChange}
                    onScroll={handleScroll}
                    spellCheck={false}
                    wrap="off"
                />
            </div>
        </div>
    );
};

export default JsonEditor;
